import { useState, useEffect, useRef } from "react";
import { message, Modal, Form } from "antd";
import { useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import sumBy from "lodash/sumBy";
import db from "../../../../database";
import useDebounce from "../../../../lib/hooks/useDebounce";
import { createCustomer, getCustomer, updateCustomer } from "../customer";
import { validatePANNo } from "../../../../services/index";
import { getApplicablePrice } from "../../../../utils/getPriceList";
import { shouldUseBatchPrice } from "../../../../utils/resolvePricingPolicy";
import { CheckoutTotalManualDiscount } from "../PricingRules/CheckoutTotalManualDiscount";
import { CheckoutFlatDiscount } from "../PricingRules/CheckoutFlatDiscount";
import { CheckoutPercentageDiscount } from "../PricingRules/CheckoutPercentageDiscount";
import upsertPOSLog from "../../Retail/posLog";
import messages from "../../../../errors/messages.en-IN.json";
import useCartStore from "../../../../stores/useCartStore";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import useReferenceStore from "../../../../stores/useReferenceStore";
import { roundTo, applyCurrencyRounding, getRoundingMethod, getPrecision } from "../../../../utils/tillHelpers";
import { findPricingRuleById, getPricingRules } from "../../../../utils/pricingRulesCache";
import { getIsInterState } from "../../../../utils/taxUtils";
import { groupItemsByTaxRate } from "../utils/taxCalculations";

const useCustomerOperations = ({
  cart,
  setCart,
  form,
  tillData,
  tillLayout,
  pricingRuleController,
  addProduct,
  removeAllDiscounts,
  posConfig,
  precision,
  taxIncludeFlag,
  orderType,
  cartRef,
  manualDiscountTypes,
  processBillDiscounts,
  openPaymentModalByCustomer,
  defaultCustomer,
  isExchange,
  searchInputRef,
  selectedSaleTypeData,
  handleDeleteLoyaltyPayment,
  hasShownCustomerModalRef,
  setShowPaymentMethods,
  verifyStock,
  posLogActivity,
  isCallCenterFlow,
  setCallCenterVisible,
  setIsCallCenterFlow,
  setShowCallCenterPage,
  guestForm,
  history,
  setActiveInput,
  setIsInputFocused,
  groupedFields,
  toCamelCase,
  retailCustomerFields,
  retailCustomerTabs,
  customerConfigData,
  keyboardInputFields,
  setKeyboardInputFields,
  setLoading,
}) => {
  // ---- Customer search state ----
  const [displayCustomerSearch, setDisplayCustomerSearch] = useState(false);
  const [displayUAECustomerSearch, setDisplayUAECustomerSearch] = useState(false);
  const [displayUAECustomer, setDisplayUAECustomer] = useState(false);
  const [customerSearchType, setCustomerSearchType] = useState(() => (posConfig?.defaultCustomerSearch === "Search Key" ? "searchKey" : "mobile"));
  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [customerSearchResults, setCustomerSearchResults] = useState();
  const [closeCustomerFlag, setCloseCustomerFlag] = useState(false);
  const [customerSearchTrigger, setCustomerSearchTrigger] = useState("normal");
  const [lastSearchedValue, setLastSearchedValue] = useState("");
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerFlag, setCustomerFlag] = useState(true);

  // ---- Customer form state ----
  const [UAECustomerForm] = Form.useForm();
  const [customerForm] = Form.useForm();
  const [displayAddNewCustomer, setDisplayAddNewCustomer] = useState(false);
  const [displayEditOldCustomer, setDisplayEditOldCustomer] = useState(false);
  const [selectedEditOldCustomer, setSelectedEditOldCustomer] = useState();

  // ---- PAN / NRI state ----
  const [isPanCustomer, setIsPanCustomer] = useState(false);
  const [panCustomerInfo, setPanCustomerInfo] = useState(null);
  const [isPanUpdateMode, setIsPanUpdateMode] = useState(false);
  // Explicit flag set true when user opens "register new customer" flow.
  // Overrides the cart.customer-based detection so persisted customers from
  // previous sessions don't make the drawer show "Update Customer" instead of
  // "Register Customer".
  const [isAddNewCustomer, setIsAddNewCustomer] = useState(false);
  const [isNriBilling, setIsNriBilling] = useState(false);
  const [panDrawerMode, setPanDrawerMode] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [isCreditSale, setIsCreditSale] = useState(false);

  // ---- B2B state ----
  const [isB2BEnabled, setIsB2BEnabled] = useState(false);
  const [selectedB2BCustomer, setSelectedB2BCustomer] = useState(null);
  const [switchToggle, setSwitchToggle] = useState(false);
  const [selectedInvoiceAddress, setSelectedInvoiceAddress] = useState(null);
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState(null);
  const [formDataCache, setFormDataCache] = useState({});
  const [isViewMode, setIsViewMode] = useState(false);
  const [viewCustomerValues, setViewCustomerValues] = useState({});
  const [isSubmittingCustomer, setIsSubmittingCustomer] = useState(false);
  const [isCustomerDrawerOpen, setIsCustomerDrawerOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  // ---- Kiosk state ----
  const [kioskUI, setKioskUI] = useState(parseFloat(localStorage.getItem("kioskUI")) ? parseFloat(localStorage.getItem("kioskUI")) : 0);
  const [kioskLogin] = Form.useForm();
  const [layoutType, setLayoutType] = useState(parseFloat(localStorage.getItem("layoutType")) ? parseFloat(localStorage.getItem("layoutType")) : 0);

  // ---- Refund state ----
  const [pendingRefundCustomer, setPendingRefundCustomer] = useState(null);
  const [isRefundCustomerEdit, setIsRefundCustomerEdit] = useState(false);
  const [isRefundWalletOpen, setIsRefundWalletOpen] = useState(false);
  const [gstInfo, setGstInfo] = useState(null);

  // ---- Active tab state ----
  const [activeTab, setActiveTab] = useState(retailCustomerTabs ? retailCustomerTabs[0]?.id : "");
  const [activeTabName, setActiveTabName] = useState("");

  // ---- Refs ----
  const customerSearchInputRef = useRef();
  const customerSubmitInFlightRef = useRef(false);
  const customerSelectionInFlightRef = useRef(false);
  const customerSearchAbortRef = useRef(null);

  // ---- Debounced customer search ----
  const debouncedCustomerSearch = useDebounce(customerSearchInput, 350);
  useEffect(() => {
    if (debouncedCustomerSearch && debouncedCustomerSearch.trim().length >= 2) {
      setLoading(true);
      handleCustomerSearch(debouncedCustomerSearch);
    } else {
      if (customerSearchAbortRef.current) {
        customerSearchAbortRef.current.abort();
        customerSearchAbortRef.current = null;
      }
      setCustomerSearchResults([]);
      setLoading(false);
    }
  }, [debouncedCustomerSearch]);

  // ---- Kiosk / layoutType listener ----
  useEffect(() => {
    const handleCustomEvent = (event) => {
      const dynamoDBValue = event.detail.newValue;
      if (event.detail.key === "kioskUI") {
        setKioskUI(dynamoDBValue ? dynamoDBValue : parseFloat(localStorage.getItem("kioskUI")) || 0);
      } else if (event.detail.key === "layoutType") {
        setLayoutType(dynamoDBValue ? dynamoDBValue : parseFloat(localStorage.getItem("layoutType")) || 0);
      }
    };

    window.addEventListener("customStorageChange", handleCustomEvent);
    return () => {
      window.removeEventListener("customStorageChange", handleCustomEvent);
    };
  }, []);

  useEffect(() => {
    if (activeTabName === "") {
      setActiveTabName("contact");
    }
  }, [retailCustomerFields]);

  // Hydrate PAN / B2B / NRI / Credit state when customer drawers open.
  // BUG-2114 (RC-1): trust the actual customer record over localStorage
  // whenever a specific existing customer is being edited — localStorage is a
  // single global flag shared across every unrelated flow in the session
  // (checkout, estimate, preorder, scanner, etc.), so it can be stale or
  // absent for the customer actually being edited. Defaulting to OFF from a
  // stale/absent flag silently strips a real B2B/PAN/credit customer's status
  // on submit; defaulting to ON from a leaked flag silently converts a real
  // B2C customer instead. Extends the same fix already applied to nriBilling
  // under BUG-1643 to its sibling flags. New-customer creation (no
  // selectedEditOldCustomer yet) keeps the prior localStorage default — there
  // is no record to trust yet.
  useEffect(() => {
    if (displayAddNewCustomer || displayEditOldCustomer) {
      // BUG-2114 (RC-1) follow-up (2026-08-25): reverted. sCustomer.sCustomerID
      // is populated for ordinary Retail customers too (confirmed live -- a
      // customerSearch result with customerCategory.name "Retail Customer"
      // still carried sCustomer.sCustomerID), so it can't be used to infer B2B
      // status. The B2B flag must only ever reflect the explicit toggle in the
      // Business tab -- never auto-derived from customer data.
      const storedB2B = localStorage.getItem("isB2bEnabled") === "true";
      setIsB2BEnabled(storedB2B);
      const storedPanCustomer = selectedEditOldCustomer
        ? !!extractPanFromMeta(selectedEditOldCustomer?.metaData) && panDrawerMode !== "VALIDATION"
        : localStorage.getItem("isPanCustomer") === "true" && panDrawerMode !== "VALIDATION";
      setIsPanCustomer(storedPanCustomer);
      // BUG-1643: when editing a specific existing customer, trust their
      // actual nriBilling value instead of localStorage — localStorage can be
      // stale from a previously edited, unrelated customer, and defaulting a
      // genuine NRI customer's toggle to OFF here would silently clear their
      // NRI status on submit. New-customer creation (no selectedEditOldCustomer
      // yet) keeps the prior localStorage default.
      const storedNriBilling = selectedEditOldCustomer ? selectedEditOldCustomer?.nriBilling === true : localStorage.getItem("isNriBilling") === "true";
      setIsNriBilling(storedNriBilling);
      const storedCreditSale = selectedEditOldCustomer
        ? selectedEditOldCustomer?.iscredit === true || selectedEditOldCustomer?.iscredit === "true"
        : useTransactionStore.getState().isCreditSale === "true" || localStorage.getItem("isCreditSale") === "true";
      setIsCreditSale(storedCreditSale);
    }
  }, [displayAddNewCustomer, displayEditOldCustomer, selectedEditOldCustomer]);

  // ---- toCamelCaseMetaData helper ----
  const toCamelCaseMetaData = (str) => {
    if (!str) return "";
    return str
      .replace(/[^a-zA-Z0-9 ]/g, "")
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => (index === 0 ? word.toLowerCase() : word.toUpperCase()))
      .replace(/\s+/g, "");
  };

  // ---- normalizeStaticSelectValue helper ----
  const normalizeStaticSelectValue = (value, staticJson) => {
    if (!value || !staticJson) return value;
    try {
      const parsed = JSON.parse(staticJson);
      if (parsed[value]) return value;
      const reverseMap = Object.fromEntries(Object.entries(parsed).map(([k, v]) => [v, k]));
      return reverseMap[value] || value;
    } catch {
      return value;
    }
  };

  // ---- convertMetaDataToKeyValue ----
  const convertMetaDataToKeyValue = (customer) => {
    const metaKeyValues = (customer.metaData || []).reduce((acc, { key, value }) => {
      const isGroupKey = /^group_\d+$/i.test(key);
      if (isGroupKey) {
        try {
          const parsedGroup = JSON.parse(value);
          const groupCamelCased = {};

          for (const groupKey in parsedGroup) {
            let fieldValue = parsedGroup[groupKey];
            const camelKey = toCamelCaseMetaData(groupKey);

            const fieldConfig = groupedFields.flatMap((g) => g.fields).find((f) => toCamelCase(f.name) === camelKey);

            if (fieldConfig?.type === "select" && fieldConfig?.dataSource?.type === "static") {
              fieldValue = normalizeStaticSelectValue(fieldValue, fieldConfig.defaultValue);
            }
            groupCamelCased[toCamelCaseMetaData(groupKey)] = fieldValue;
          }

          acc[key] = groupCamelCased;
        } catch (e) {
          console.error("Failed to parse group value:", value);
        }
      } else {
        const camelKey = toCamelCaseMetaData(key);
        let fieldValue = value;

        const fieldConfig = groupedFields.flatMap((g) => g.fields).find((f) => toCamelCase(f.name) === camelKey);

        if (fieldConfig?.type === "select" && fieldConfig?.dataSource?.type === "static") {
          fieldValue = normalizeStaticSelectValue(fieldValue, fieldConfig.defaultValue);
        }

        acc[camelKey] = fieldValue;
      }
      return acc;
    }, {});
    const result = {
      ...metaKeyValues,
      ...customer,
      metaData: [],
    };
    if (retailCustomerFields?.length > 0) {
      retailCustomerFields.forEach((field) => {
        const lowerName = field.name?.toLowerCase() || "";
        const formKey = toCamelCase(field.name);
        if (lowerName.includes("search key") && customer.code && !result[formKey]) {
          result[formKey] = customer.code;
        }
        if (lowerName.includes("loyalty number") && customer.cardNumber && !result[formKey]) {
          result[formKey] = customer.cardNumber;
        }
      });
    }
    return result;
  };

  // ---- PAN helpers ----
  const autoEnablePanCustomer = (formValues) => {
    const panField = groupedFields
      ?.flatMap((g) =>
        g.fields.map((f) => ({
          name: f.name,
          groupId: g.groupId,
        }))
      )
      .find((f) => f.name?.toLowerCase().startsWith("pan"));

    if (!panField) return;

    const key = toCamelCase(panField.name);

    let panValue;

    if (panField.groupId) {
      panValue = formValues?.[String(panField.groupId).toLowerCase()]?.[key];
    } else {
      panValue = formValues?.[key];
    }

    if (panValue && panValue.trim() !== "") {
      setIsPanCustomer(true);
      localStorage.setItem("isPanCustomer", "true");
    }
  };

  const autoEnableNriBilling = (formValues) => {
    const nriField = groupedFields
      ?.flatMap((g) =>
        g.fields.map((f) => ({
          name: f.name,
          groupId: g.groupId,
        }))
      )
      .find((f) => {
        const fieldName = f.name?.toLowerCase().replace(/\s+/g, "");
        return fieldName?.startsWith("passport") || fieldName?.startsWith("immigration") || fieldName?.startsWith("visa");
      });

    if (!nriField) return;

    const key = toCamelCase(nriField.name);

    let nriValue;

    if (nriField.groupId) {
      nriValue = formValues?.[String(nriField.groupId).toLowerCase()]?.[key];
    } else {
      nriValue = formValues?.[key];
    }

    if (nriValue && nriValue.trim() !== "") {
      setIsNriBilling(true);
      localStorage.setItem("isNriBilling", "true");
      setIsPanCustomer(false);
      localStorage.setItem("isPanCustomer", "false");
    }
  };

  const validateAndSavePanForCustomer = async (customer) => {
    const panValue = customer?.metaData?.find((m) => m.key === "Pan Card")?.value;
    if (!panValue) return null;

    setLoading(true);
    try {
      const panApi = await validatePANNo(panValue);
      if (!panApi) return null;

      const realStatus = getPanRealStatus({
        panStatusCode: panApi.panStatusCode,
        aadhaarSeedingStatusCode: panApi.aadhaarSeedingStatusCode,
        isIndividual: panApi.isIndividual,
        isIndividualAPI: panApi.isIndividualAPI,
      });

      const isPanUsable = realStatus === "VALID & OPERATIVE" || realStatus === "VALID & INOPERATIVE";

      return {
        panNo: panApi.panNo,
        panStatus: panApi.panStatus,
        aadhaarSeedingStatusCode: panApi.aadhaarSeedingStatusCode,
        isValid: isPanUsable,
        realStatus,
      };
    } catch (err) {
      console.error("PAN validation failed:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const extractPanFromMeta = (metaData = []) => {
    if (!Array.isArray(metaData)) return null;

    // Match only keys that hold the actual PAN number — "Pan Card", "Pan No", "PAN_CARD", etc.
    // Must NOT match "Pan Status", "Pan Status Code" whose values are status codes like "E", "VALID".
    const panKeyPatterns = ["pan card", "pan no", "pan_card", "pan_no", "panno", "pancard"];
    const panEntry = metaData.find(
      (item) =>
        typeof item.key === "string" &&
        panKeyPatterns.some(
          (pat) =>
            item.key.toLowerCase().replace(/\s+/g, " ").trim() === pat ||
            item.key
              .toLowerCase()
              .replace(/[\s_]+/g, "")
              .trim() === pat.replace(/[\s_]+/g, "")
        ) &&
        typeof item.value === "string" &&
        item.value.trim() !== "" &&
        item.value.trim().length >= 10 // PAN is always 10 chars — skip short status codes
    );

    return panEntry?.value?.trim() || null;
  };

  const getPanRealStatus = ({ panStatusCode, aadhaarSeedingStatusCode, isIndividual, isIndividualAPI }) => {
    const disabledPanStatusMap = {
      N: "INVALID PAN",
      I: "PAN INOPERATIVE",
      X: "PAN DEACTIVATED",
      F: "FAKE PAN",
      D: "PAN DELETED",
      EA: "PAN AMALGAMATION",
      EC: "PAN ACQUISITION",
      ED: "DEATH OF PAN HOLDER",
      EI: "PAN DISSOLVED",
      EL: "PAN LIQUIDATED",
      EM: "PAN MERGER",
      EP: "PAN PARTITION",
      ES: "PAN SPLIT",
      EU: "PAN UNDER LIQUIDATION",
    };

    if (disabledPanStatusMap[panStatusCode]) {
      return disabledPanStatusMap[panStatusCode];
    }

    if (panStatusCode === "E") {
      if (aadhaarSeedingStatusCode === "Y") {
        return "VALID & OPERATIVE";
      }

      if (isIndividual === false || isIndividualAPI === false || aadhaarSeedingStatusCode === "NA" || aadhaarSeedingStatusCode == null) {
        return "VALID & OPERATIVE";
      }

      return "VALID & INOPERATIVE";
    }

    return "INVALID PAN";
  };

  // ---- Customer search / selection ----
  const closeCustomerSearch = (obj) => {
    setDisplayCustomerSearch(false);
    setDisplayUAECustomerSearch(false);
    setCustomerSearchInput("");
    setCustomerSearchResults([]);
    setIsInputFocused(false);
    setCustomerSearchTrigger("normal");
    if (searchInputRef?.current) {
      searchInputRef?.current?.focus();
    }
  };

  const handleCustomerSearch = async (searchValue) => {
    if (customerSearchAbortRef.current) {
      customerSearchAbortRef.current.abort();
    }
    const controller = new AbortController();
    customerSearchAbortRef.current = controller;
    try {
      await getCustomer(
        form,
        tillLayout,
        searchValue,
        setKioskUI,
        setCustomerSearchResults,
        kioskLogin,
        tillData,
        pendingRefundCustomer,
        setPendingRefundCustomer,
        isRefundCustomerEdit,
        setIsRefundCustomerEdit,
        { signal: controller.signal }
      );
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") return;
    } finally {
      if (customerSearchAbortRef.current === controller) {
        setLoading(false);
      }
    }
  };

  const proceedWithCustomerSelection = async (selectedCustomer, index) => {
    if (customerSelectionInFlightRef.current) return;
    customerSelectionInFlightRef.current = true;
    try {
    // BUG-1643: NRI status must come only from the customer's explicit "Nri
    // Billing" metaData flag. The old passport+visa heuristic fallback tagged
    // ordinary (non-NRI, no-PAN) customers as NRI whenever they had any
    // passport/immigration KYC field on file, routing them into the flat-rate
    // NRI TCS branch which skips PAN validation entirely — confirmed live for
    // a customer with Passport Number + Immigration Date but no PAN and no
    // explicit NRI flag, who was auto-tagged NRI and had TCS applied with zero
    // validation.
    const nriBillingMeta = selectedCustomer?.metaData?.find((m) => m?.key?.toLowerCase().replace(/\s+/g, "") === "nribilling");
    const isNRI = nriBillingMeta?.value != null && nriBillingMeta?.value !== "" ? String(nriBillingMeta.value).toLowerCase() === "true" : false;
    // BUG-1643: TCS must only apply after PAN is validated in THIS transaction —
    // no exceptions, including exchange/return (explicit client requirement).
    // Do NOT read the customer's cached "Customer Properties" backend metaData
    // here: that blob reflects whatever validation happened at some point in the
    // PAST (possibly a different transaction, days ago), and trusting it let TCS
    // silently auto-apply on selection alone with zero in-session confirmation.
    // customerProperties must come only from an actual validation performed this
    // session (CustomerDrawerFooter / customer.js create-or-update paths), so a
    // fresh search-selected customer starts with whatever (if anything) is
    // already attached to the object — never re-derived from stale metaData.
    const selectedCustomerProperties = selectedCustomer?.customerProperties;
    if (isExchange && isPanCustomer) {
      const currentExchangeCart = useCartStore.getState().exchangeCart;
      if (currentExchangeCart) {
        useCartStore.getState().setExchangeCart({ ...currentExchangeCart, tcs: 0 });
      }
      setSelectedEditOldCustomer(selectedCustomer);
      await removeCutomer();
      setIsPanCustomer(false);
    } else if (isExchange) {
      setSelectedEditOldCustomer(selectedCustomer);
    }
    if (useTransactionStore.getState().refundCustomerEdit === "Y" && cart?.isReturn === true) {
      useTransactionStore.getState().setRefundReturnCustomerId(selectedCustomer);

      useTransactionStore.getState().setRefundCustomerEdit(null);
      setIsRefundWalletOpen(true);
      closeCustomerSearch();
      return;
    }
    setCustomerFlag(false);
    const isExchangeFlow = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
    if (isExchangeFlow && isPanCustomer) {
      setIsPanCustomer(false);
    }
    const exchangeCartForSelection = isExchangeFlow ? useCartStore.getState().exchangeCart : null;
    let latestData = isExchangeFlow && exchangeCartForSelection?.items?.length > 0 ? { ...exchangeCartForSelection } : await removeAllDiscounts(false, true);
    const truncateDecimal = (num, decimalPlaces) => {
      let numStr = num.toString();
      let decimalIndex = numStr.indexOf(".");
      if (decimalIndex === -1 || numStr.length - decimalIndex - 1 <= decimalPlaces) {
        return num;
      }
      numStr = numStr.slice(0, decimalIndex + decimalPlaces + 1);
      return parseFloat(numStr);
    };

    let num = customerSearchResults[index]?.retlLoyaltyBalance || 0;
    let truncatedNum = truncateDecimal(num, 2);

    const selectedCustomerData = { ...customerSearchResults[index], retlLoyaltyBalance: truncatedNum };
    // BUG-2114 (RC-4) follow-up (2026-08-25): reverted the auto-set-to-true
    // half of this fix. sCustomer.sCustomerID is populated for ordinary Retail
    // customers too (confirmed live, KWC 2026-08-25: a plain mobile-number
    // search returned a "Retail Customer"-category result that still carried
    // sCustomer.sCustomerID), so its presence mis-flagged a genuine B2C order
    // as B2B (b2bOrder: "Y" sent to posOrderProcessor1 for a walk-in retail
    // sale). The B2B flag must only ever be set by the explicit toggle in the
    // Add/Edit Customer drawer's Business tab -- plain search-and-select is
    // never itself a B2B declaration. Still clear any stale flag left over
    // from a previously selected B2B customer this session, mirroring
    // removeCutomer's existing clear.
    localStorage.removeItem("isB2bEnabled");
    useTransactionStore.getState().setIsB2bEnabled(null);
    const saleTypeData1 = usePOSConfigStore.getState().saleTypeData || {};
    const selectedPriceListId1 = selectedCustomerData?.sPriceListId ?? saleTypeData1?.customer?.sPriceListId;
    const cartObj = {
      ...latestData,
      customer: {
        ...selectedCustomerData,
        sPriceListId: selectedPriceListId1,
        nriBilling: isNRI,
        customerProperties: selectedCustomerProperties,
      },
    };
    let updatedCart = cartObj;

    if (cartObj.items?.length > 0) {
      await Promise.all(
        cartObj.items.map(async (ele) => {
          let addToCart = ele;
          let matchedPriceIndex = [];
          if (defaultCustomer?.cwrCustomerId !== updatedCart?.customer?.cwrCustomerId) {
            const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
            let taxIncludeFlagLocal =
              saleTypeData?.customer?.istaxincluded === "Y" ? "Y" : tillData.tillAccess.csBunit.isTaxIncluded ? tillData.tillAccess.csBunit.isTaxIncluded : "Y";

            if (addToCart?.priceList?.length > 0) {
              const selectedPrice = getApplicablePrice({
                priceList: addToCart.priceList,
                selectedUomId: addToCart.uom || addToCart.selectedUomId || addToCart.uomData?.[0]?.csUomId,
                sPriceListId: updatedCart.customer.sPriceListId,
                alternateUom: addToCart?.alternateUom || [],
                primaryUomId: addToCart?.primaryUomId || addToCart?.csUomId || addToCart?.uomData?.[0]?.csUomId || null,
              });
              if (selectedPrice && addToCart?.posPriceEdit !== "Y" && !(shouldUseBatchPrice(addToCart) && addToCart.mBatchId)) {
                addToCart.price = selectedPrice.pricestd;
                addToCart.salePrice = selectedPrice.pricestd;
                addToCart.realPrice = selectedPrice.pricestd;
                addToCart.sunitprice = selectedPrice.pricestd;
              } else if (!updatedCart.customer.sPriceListId && !(shouldUseBatchPrice(addToCart) && addToCart.mBatchId)) {
                addToCart.price = addToCart.originalPrice;
                addToCart.salePrice = addToCart.originalPrice;
                addToCart.realPrice = addToCart.originalPrice;
                addToCart.sunitprice = addToCart.originalPrice;
              }
            }
            if (Array.isArray(addToCart.children) && addToCart.children.length > 0) {
              addToCart.children.forEach((child) => {
                if (child.priceList?.length > 0 && !child.mBatchId) {
                  const childPrice = getApplicablePrice({
                    priceList: child.priceList,
                    selectedUomId: child.uom || child.selectedUomId || child.uomData?.[0]?.csUomId,
                    sPriceListId: updatedCart.customer.sPriceListId,
                    alternateUom: child?.alternateUom || [],
                    primaryUomId: child?.primaryUomId || child?.csUomId || child?.uomData?.[0]?.csUomId || null,
                  });
                  if (childPrice && !(shouldUseBatchPrice(child) && child.mBatchId)) {
                    child.price = childPrice.pricestd;
                    child.salePrice = childPrice.pricestd;
                    child.realPrice = childPrice.pricestd;
                    child.sunitprice = childPrice.pricestd;
                  } else if (!updatedCart.customer.sPriceListId && !(shouldUseBatchPrice(child) && child.mBatchId)) {
                    child.price = child.originalPrice || child.sunitprice;
                    child.salePrice = child.originalPrice || child.sunitprice;
                    child.realPrice = child.originalPrice || child.sunitprice;
                    child.sunitprice = child.originalPrice || child.sunitprice;
                  }
                }
                const sp = parseFloat(child.sunitprice);
                const childMrp = sp * child.weight;
                const childTax = taxIncludeFlagLocal === "Y" ? childMrp - childMrp / (1 + child.taxRate / 100) : (childMrp * child.taxRate) / 100;
                child.taxAmount = childTax;
                child.nettotal = taxIncludeFlagLocal === "Y" ? parseFloat(childMrp.toFixed(precision)) : parseFloat((childMrp + childTax).toFixed(precision));
              });
              addToCart.taxAmount = sumBy(addToCart.children, "taxAmount");
              addToCart.nettotal = sumBy(addToCart.children, "nettotal");
            } else {
              // A line carrying a manual line-level OR total-bill-level discount was already
              // recomputed correctly by removeAllDiscounts (called above): nettotal is net-of-discount
              // AND taxAmount is on the discounted net. Recomputing here from salePrice recomputes tax
              // on the pre-discount price (batch price reset restores full salePrice) — inflating
              // taxAmount/total by the tax-on-discount. Skip these lines so the already-correct
              // nettotal/taxAmount are preserved. (TIC-5459: totalLevelDiscount was missing from this
              // guard, causing a double-subtraction on any item under an active Total Bill Discount.)
              const hasManualLineDiscount = updatedCart?.manualDiscountData?.lineLevelDiscount?.some((d) => d.lineId === addToCart.lineId);
              const hasManualTotalDiscount = updatedCart?.manualDiscountData?.totalLevelDiscount?.length > 0;
              if (!hasManualLineDiscount && !hasManualTotalDiscount) {
                const addOnsPriceSum = sumBy(addToCart?.selectedAddons, "price");
                const mrp = (parseFloat(addToCart.salePrice) + addOnsPriceSum * addToCart.weight) * parseFloat(addToCart.weight);
                const tax = taxIncludeFlagLocal === "Y" ? mrp - mrp / (1 + addToCart.taxRate / 100) : mrp * (addToCart.taxRate / 100);
                addToCart.taxAmount = tax;
                addToCart.nettotal = parseFloat(
                  parseFloat(
                    (taxIncludeFlagLocal === "Y"
                      ? mrp - parseFloat((addToCart.discount || 0).toFixed(precision))
                      : mrp - parseFloat((addToCart.discount || 0).toFixed(precision)) + tax
                    ).toFixed(precision)
                  )
                );
              }
            }

            updatedCart = await pricingRuleController(addToCart, cartObj, cartObj, setCart, cartObj, orderType);
            return updatedCart;
          }
        })
      );
    } else {
      let orderTimeDetails = usePOSConfigStore.getState().orderTimeDetails || { orderStartTime: "", orderEndTime: "", paymentStartTime: "" };
      orderTimeDetails = {
        ...orderTimeDetails,
        orderStartTime: dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss"),
      };
      usePOSConfigStore.getState().setOrderTimeDetails(orderTimeDetails);
    }

    if (updatedCart?.couponInput?.length > 0) {
      let addToCart = cartObj.items[0];
      await Promise.all(
        updatedCart.couponInput.map(async (coupon) => {
          const matchingPricingRules = findPricingRuleById(coupon.mPricingruleId);
          if (matchingPricingRules[0].type !== "TD" && matchingPricingRules[0].type !== "TDF") {
            updatedCart = await pricingRuleController(
              addToCart,
              updatedCart,
              cart,
              setCart,
              cartRef,
              orderType,
              true,
              coupon.couponCode,
              coupon.referenceId,
              coupon.mPricingCouponId,
              coupon.mPricingruleId
            );
          } else {
            updatedCart = await processBillDiscounts(
              matchingPricingRules[0],
              updatedCart,
              true,
              coupon.couponCode,
              coupon.referenceId,
              coupon.mPricingCouponId,
              coupon.mPricingruleId
            );
          }
        })
      );
    }
    if (cartObj.totalDiscountFlag) {
      await openPaymentModalByCustomer(updatedCart);
    }
    let updatedTotalTax = 0;
    let updatedTotalPrice = 0;
    let updatedTotalItemsQty = 0;
    let updatedTotalDiscounts = 0;

    if (updatedCart?.manualDiscountData?.lineLevelDiscount?.length > 0) {
      for (const item of updatedCart.manualDiscountData.lineLevelDiscount) {
        // Fall back to the always-loaded pricing-rules cache: manualDiscountTypes state is only
        // populated when the Manual Discount modal is opened, so on a fresh load (or restored cart)
        // it can be empty — which would silently drop the manual line discount on re-apply.
        const pricingRule =
          manualDiscountTypes.find((md) => md.mPricingrulesId === item.pricingRule) ||
          getPricingRules().find((r) => r.mPricingrulesId === item.pricingRule);
        let productSelected = updatedCart.items.filter((cartItem) => cartItem.productId === item.productId);
        if (pricingRule?.type === "FD") {
          try {
            const newCart = await CheckoutFlatDiscount(productSelected[0], pricingRule, setCart, updatedCart, orderType, updatedCart, item.discountValue);
            updatedCart = newCart;
          } catch (err) {
            console.error("Error applying flat discount:", err);
          }
        } else if (pricingRule?.type === "PD") {
          try {
            const newCart = await CheckoutPercentageDiscount(productSelected[0], pricingRule, setCart, updatedCart, orderType, updatedCart, item.discountValue);
            updatedCart = newCart;
          } catch (err) {
            console.error("Error applying percentage discount:", err);
          }
        }
      }
    }

    if (updatedCart?.manualDiscountData?.totalLevelDiscount?.length > 0) {
      for (const item of updatedCart.manualDiscountData.totalLevelDiscount) {
        // Fall back to the always-loaded pricing-rules cache: manualDiscountTypes state is only
        // populated when the Manual Discount modal is opened, so on a fresh load (or restored cart)
        // it can be empty — which would silently drop the manual line discount on re-apply.
        const pricingRule =
          manualDiscountTypes.find((md) => md.mPricingrulesId === item.pricingRule) ||
          getPricingRules().find((r) => r.mPricingrulesId === item.pricingRule);
        if (pricingRule?.type === "TD") {
          try {
            const newCart = await CheckoutTotalManualDiscount(pricingRule, setCart, updatedCart, orderType, updatedCart, item.discountValue);
            updatedCart = newCart;
          } catch (err) {
            console.error("Error applying Total discount:", err);
          }
        }
      }
    }

    const itemSummery = {
      decimalItems: { totalQty: 0, totalAmount: 0 },
      nonDecimalItems: { totalQty: 0, totalAmount: 0 },
      itemsQty: 0,
      items: 0,
    };

    const isInterState = getIsInterState(updatedCart);
    const groupedItemsByTaxRate = groupItemsByTaxRate(updatedCart.items, precision, { isInterState });

    updatedCart.items = updatedCart.items.map((item, i) => {
      const precisionVal = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
      const nettotalFixed = parseFloat(parseFloat(item?.nettotal)?.toFixed(precisionVal));
      const taxAmountFixed = parseFloat(parseFloat(item?.taxAmount)?.toFixed(precisionVal));
      const discountFixed = item.discount ? parseFloat(parseFloat(item?.discount)?.toFixed(precisionVal)) : 0;

      updatedTotalPrice += item.nettotal || 0;
      updatedTotalItemsQty += item.weight;
      updatedTotalTax += item.taxAmount || 0;
      updatedTotalDiscounts += discountFixed;

      item.discount = discountFixed;
      item.key = i;

      if (!item.isGiftCard) {
        const unitPrice = isFinite(item.nettotal / item.weight) ? (taxIncludeFlag === "Y" ? item.nettotal / item.weight / (1 + item.taxRate / 100) : item.salePrice) : 0;

        const lineTax = item.listPrice * (1 + (item.taxRate || 0) / 100);
        const stdTax = item.sunitprice * (1 + (item.taxRate || 0) / 100);
        const grossUnit = Math.abs(item.sunitprice) - Math.abs(item.discount) / Math.abs(item.weight);
        const netList =
          taxIncludeFlag === "Y" ? item.listPrice - parseFloat(parseFloat(item.listPrice - item.listPrice / (1 + item.taxRate / 100))?.toFixed(precisionVal)) : item.listPrice;
        const gross_list = taxIncludeFlag === "Y" ? item.listPrice : parseFloat(parseFloat(lineTax)?.toFixed(precisionVal));
        const grossStd = taxIncludeFlag === "Y" ? item.sunitprice : stdTax;

        Object.assign(item, {
          linetax: nettotalFixed ? taxAmountFixed : 0,
          linenet: nettotalFixed ? parseFloat(parseFloat(item.nettotal - item.taxAmount)?.toFixed(precisionVal)) : 0,
          linegross: nettotalFixed || 0,
          linestd: nettotalFixed ? parseFloat(parseFloat(grossStd * item.weight)?.toFixed(precisionVal)) : 0,
          netunit: nettotalFixed ? parseFloat(parseFloat(unitPrice)?.toFixed(precisionVal)) : 0,
          listprice: nettotalFixed ? item.listPrice : 0,
          grossunit: nettotalFixed ? parseFloat(parseFloat(unitPrice)?.toFixed(precisionVal)) * (item.isReturn ? -1 : 1) : 0,
          grossstd: nettotalFixed ? parseFloat(parseFloat(grossStd)?.toFixed(precisionVal)) : 0,
          grosslist: nettotalFixed ? roundTo(gross_list, precisionVal) : 0,
          netList: nettotalFixed ? netList : 0,
          unitPrice: nettotalFixed ? parseFloat(parseFloat(unitPrice)?.toFixed(precisionVal)) : 0,
          taxAmount: nettotalFixed ? taxAmountFixed : 0,
        });
      }

      // Update summary
      if (Array.isArray(item.children) && item.children.length > 0) {
        item.children.forEach((child) => {
          const isDecimal = child.uomData?.[0]?.decimal === "Y" || child.isDecimal;
          const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";

          itemSummery.itemsQty += child.weight;
          itemSummery.items += 1;
          itemSummery[summaryKey].totalQty += child.weight;
          itemSummery[summaryKey].totalAmount += parseFloat(parseFloat(child.nettotal)?.toFixed(precisionVal));
        });
      } else {
        const isDecimal = item.uomData?.[0]?.decimal === "Y" || item.isDecimal;
        const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";

        itemSummery.itemsQty += item.weight;
        itemSummery.items += 1;
        itemSummery[summaryKey].totalQty += item.weight;
        itemSummery[summaryKey].totalAmount += parseFloat(parseFloat(item.nettotal)?.toFixed(precisionVal));
      }
      return item;
    });

    const sumLineTotals = (lines) => {
      let totalLineGross = 0;
      let totalLineTax = 0;

      lines.forEach((line) => {
        totalLineGross += line.nettotal;
        totalLineTax += line.taxAmount;
      });

      return { totalLineGross, totalLineTax };
    };
    const adjustOrderTotals = (order, lines) => {
      const { totalLineGross, totalLineTax } = sumLineTotals(lines);
      let adjustedOrderGross = order.total;
      let adjustedOrderTax = order.tax;

      const grossDifference = totalLineGross - adjustedOrderGross;
      const taxDifference = totalLineTax - adjustedOrderTax;

      // If grossDifference equals the existing roundOff, the gap is due to intentional
      // currency rounding — preserve the rounded total instead of correcting it back.
      // grossDifference = gross - rounded; roundOff = rounded - gross; sum = 0 when equal.
      const existingRoundOff = order.roundOff || 0;
      const isRoundingDifference = Math.abs(grossDifference + existingRoundOff) < 0.01;

      if (Math.abs(grossDifference) >= 0.005 && !isRoundingDifference) {
        adjustedOrderGross += grossDifference;
      }

      if (Math.abs(taxDifference) >= 0.005) {
        adjustedOrderTax += taxDifference;
      }

      return {
        ...order,
        total: adjustedOrderGross,
        tax: adjustedOrderTax,
      };
    };
    updatedCart = adjustOrderTotals(updatedCart, updatedCart.items);
    const roundingMethod = getRoundingMethod(tillData);
    let finalTotal = updatedCart.total;
    let finalRoundOff = updatedCart.roundOff || 0;
    if (roundingMethod !== "NR") {
      const { roundedTotal, roundOffAmount } = applyCurrencyRounding(updatedTotalPrice, roundingMethod, getPrecision(tillData));
      finalTotal = roundedTotal;
      finalRoundOff = parseFloat(parseFloat(roundOffAmount).toFixed(precision));
    }
    const allItemsInCart = (updatedCart.items || []).flatMap((item) => (Array.isArray(item.children) && item.children.length > 0 ? item.children : [item]));
    const updatedTotalStd = allItemsInCart.reduce((sum, item) => sum + (item.linestd || 0), 0);
    const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
    const selectedPriceListId = customerSearchResults[index]?.sPriceListId ?? saleTypeData?.customer?.sPriceListId;
    let finalCartObj = {
      ...updatedCart,
      items: [...updatedCart.items],
      discount: updatedTotalDiscounts,
      totalQty: updatedTotalItemsQty,
      total: finalTotal,
      roundOff: finalRoundOff,
      tax: roundTo(updatedTotalTax, getPrecision(tillData)),
      customer: {
        ...customerSearchResults[index],
        sPriceListId: selectedPriceListId,
        nriBilling: isNRI,
        customerProperties: selectedCustomerProperties,
      },
      totalStd: updatedTotalStd,
      itemsSummery: itemSummery,
      groupedItemsByTaxRate: groupedItemsByTaxRate,
    };
    if (isExchange) {
      useCartStore.getState().setExchangeCart(finalCartObj);
    } else {
      setCart({ ...finalCartObj });
    }
    try {
      guestForm.setFieldsValue({
        guestName: customerSearchResults[index]?.name || "",
        contactNumber: customerSearchResults[index]?.mobileNo || "",
      });
    } catch (e) {
    }
    if (isCallCenterFlow && tillLayout === 2) {
      history.push("/call-center");
      setCallCenterVisible(true);
      setShowCallCenterPage(false);
      setDisplayAddNewCustomer(false);
      setDisplayCustomerSearch(false);
      setCustomerSearchInput("");
      return;
    }
    const wasVerifyStockTrigger = customerSearchTrigger === "verifyStock";
    closeCustomerSearch(finalCartObj, true);
    setIsInputFocused(false);
    upsertPOSLog(finalCartObj, "ACT");

    if (wasVerifyStockTrigger && verifyStock) {
      // Continue checkout flow: customer is now set, re-run verifyStock to proceed to payment
      //verifyStock();
    } else {
      setShowPaymentMethods(false);
      setActiveInput("search");
      if (searchInputRef?.current) {
        searchInputRef?.current?.focus();
      }
    }
    } finally {
      customerSelectionInFlightRef.current = false;
    }
  };

  const selectCustomer = async (index) => {
    const selectedCustomer = customerSearchResults[index];
    if (!selectedCustomer?.mobileNo && retailCustomerFields && retailCustomerFields.length > 0) {
      Modal.confirm({
        title: "No Mobile Number",
        content: "Do you want to add customer without mobile number?",
        okText: "Proceed",
        cancelText: "Cancel",
        okButtonProps: { style: { display: "inline-block" } },
        onOk: async () => {
          await proceedWithCustomerSelection(selectedCustomer, index);
        },
        onCancel() {},
      });
      return;
    }
    await proceedWithCustomerSelection(selectedCustomer, index);
  };

  // ---- Add new customer ----
  const showAddNewCustomerFields = async () => {
    setDisplayCustomerSearch(false);
    // Reset all "edit mode" flags so the form opens in clean "register new" state.
    // Without this, leftover state from a previous edit session causes the form to
    // show "Update Customer" button instead of "Register Customer".
    setIsPanUpdateMode(false);
    setIsAddNewCustomer(true);
    setSelectedEditOldCustomer(null);
    // Clear any document state carried over from a prior edit/view session so the
    // new-customer form opens with an empty upload state (no stale "Uploaded" badge).
    setDocuments([]);
    setIsB2BEnabled(false);
    setIsNriBilling(false);
    setIsCreditSale(false);
    setPanCustomerInfo(null);
    setIsPanCustomer(false);
    localStorage.removeItem("isPanCustomer");

    const searchResult = customerSearchInput || keyboardInputFields?.search;
    let customerSearchTypeLocal = "name";
    const isAlphaNumericNoSpace = (value) => /[a-zA-Z]/.test(value) && /[0-9]/.test(value);

    if (/^[a-zA-Z0-9.!#$%&'+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)$/.test(searchResult)) {
      customerSearchTypeLocal = "email";
    } else if (/^\d+$/.test(searchResult)) {
      customerSearchTypeLocal = "mobile";
    } else if (isAlphaNumericNoSpace(searchResult)) {
      customerSearchTypeLocal = "pan";
    } else {
      customerSearchTypeLocal = "name";
    }

    const defaultAddress = {
      country: tillData.tillAccess.csBunit.customerAddress?.csCountry?.name,
      state: tillData.tillAccess.csBunit.customerAddress?.csRegion?.name,
    };

    let fieldValues = {
      ...defaultAddress,
    };

    if (retailCustomerFields && retailCustomerFields.length > 0) {
      const fieldMap = {
        name: null,
        mobile: null,
        email: null,
        pan: null,
      };
      const isPanField = (name) => {
        const clean = name.toLowerCase().replace(/\s+/g, "");
        return clean === "pancard" || clean === "panno" || clean === "pannumber";
      };

      retailCustomerFields.forEach((field) => {
        const fieldName = field.name.toLowerCase().replace(/\s+/g, "");

        if (fieldName.includes("name")) fieldMap.name = field.name;
        if (fieldName.includes("mobile") || fieldName.includes("phone")) fieldMap.mobile = field.name;
        if (fieldName.includes("email")) fieldMap.email = field.name;
        if (isPanField(field.name)) fieldMap.pan = field.name;
      });

      if (customerSearchTypeLocal === "mobile" && fieldMap.mobile) {
        fieldValues[fieldMap.mobile] = searchResult;
      }

      if (customerSearchTypeLocal === "name" && fieldMap.name) {
        fieldValues[fieldMap.name] = searchResult;
      }

      if (customerSearchTypeLocal === "email" && fieldMap.email) {
        fieldValues[fieldMap.email] = searchResult;
      }

      if (customerSearchTypeLocal === "pan" && fieldMap.pan) {
        fieldValues[fieldMap.pan] = searchResult;
      }
    } else {
      fieldValues = {
        mobile: customerSearchTypeLocal === "mobile" ? searchResult : "",
        name: customerSearchTypeLocal === "name" ? searchResult : "",
        email: customerSearchTypeLocal === "email" ? searchResult : "",
        gstNo: customerSearchTypeLocal === "pan" ? searchResult : "",
        type: "addCustomerForm",
        ...defaultAddress,
      };
    }
    setKeyboardInputFields(fieldValues);
    customerForm.setFieldsValue(fieldValues);

    const loyaltyData = await db.loyalityData.toArray();
    const defaultLoyalty = loyaltyData.find((item) => item.isDefault === "Y");

    if (defaultLoyalty) {
      customerForm.setFieldsValue({ program: defaultLoyalty.loyaltylevelId });
    }

    setDisplayAddNewCustomer(true);
  };

  const showAddNewUAECustomerFields = async () => {
    setDisplayUAECustomerSearch(false);

    const customerSearchTypeLocal = /^[a-zA-Z0-9.!#$%&'+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)$/.test(customerSearchInput)
      ? "email"
      : /^\d+$/.test(customerSearchInput)
        ? "mobile"
        : "name";

    const defaultAddress = {
      country: tillData.tillAccess.csBunit.customerAddress?.csCountry?.name,
      state: tillData.tillAccess.csBunit.customerAddress?.csRegion?.name,
    };

    const fieldValues = {
      mobile: customerSearchTypeLocal === "mobile" ? customerSearchInput : "",
      name: customerSearchTypeLocal === "name" ? customerSearchInput : "",
      email: customerSearchTypeLocal === "email" ? customerSearchInput : "",
      type: "UAECustomerForm",
      ...defaultAddress,
    };
    setKeyboardInputFields(fieldValues);
    const registrationTypes = useReferenceStore.getState().registrationTypes;
    UAECustomerForm.setFieldsValue({ ...fieldValues, type: registrationTypes?.[0]?.value || "" });

    const loyaltyData = await db.loyalityData.toArray();
    const defaultLoyalty = loyaltyData.find((item) => item.isDefault === "Y");

    if (defaultLoyalty) {
      form.setFieldsValue({ program: defaultLoyalty.loyaltylevelId });
    }

    setDisplayUAECustomer(true);
  };

  const addNewCustomer = async (data) => {
    if (customerSubmitInFlightRef.current) return;
    customerSubmitInFlightRef.current = true;
    try {
    const errorMessage = (title, content) => {
      Modal.error({ title, content });
    };

    // Mirrors the data.mobile || data.mobileno convention already used throughout
    // createCustomer() (customer.js) — the UAE form submits "mobile", not "mobileno",
    // so checking mobileno alone always looked like a missing mobile number for that form.
    const mobileValue = data?.mobile || data?.mobileno;
    if (retailCustomerFields && retailCustomerFields.length > 0 && (!mobileValue || mobileValue.trim() === "")) {
      return new Promise((resolve, reject) => {
        Modal.confirm({
          title: "No Mobile Number",
          content: "Do you want to add customer without mobile number?",
          okText: "Proceed",
          cancelText: "Cancel",
          okButtonProps: { style: { display: "inline-block" } },
          onOk: async () => {
            try {
              // Online-Only mode (disableOfflineBilling="Y") blocks when the device is
              // genuinely offline. In offline-capable mode (default), skip the navigator.onLine
              // gate entirely and let the actual API call succeed/fail on its own — navigator.onLine
              // is unreliable (see utils/serverReachability.js) and was blocking customer creation
              // with a false "check your network" error even when the backend was reachable.
              if (posConfig?.disableOfflineBilling === "Y" && !navigator.onLine) {
                errorMessage("Sync Failed", "Please check your network connection and try again");
              } else {
                await createCustomer(
                  data,
                  cart,
                  setCart,
                  form,
                  tillLayout,
                  closeCustomerSearch,
                  tillData,
                  posLogActivity,
                  setKioskUI,
                  setDisplayAddNewCustomer,
                  UAECustomerForm,
                  customerForm,
                  setTillLink,
                  setCustomerLoading,
                  setDisplayUAECustomer,
                  isCallCenterFlow,
                  setCallCenterVisible,
                  setIsCallCenterFlow,
                  history,
                  setIsSubmittingCustomer,
                  setLastSearchedValue,
                  setSelectedB2BCustomer,
                  selectedB2BCustomer,
                  setSwitchToggle,
                  setFormDataCache,
                  isPanCustomer,
                  setIsPanCustomer,
                  isB2BEnabled,
                  setPanCustomerInfo,
                  panCustomerInfo,
                  pendingRefundCustomer,
                  setPendingRefundCustomer,
                  isRefundCustomerEdit,
                  setIsRefundCustomerEdit,
                  isRefundWalletOpen,
                  setIsRefundWalletOpen,
                  isNriBilling,
                  setIsNriBilling,
                  isCreditSale,
                  setIsCreditSale,
                  panDrawerMode,
                  documents,
                  setGstInfo,
                  retailCustomerFields,
                  gstInfo,
                  selectedInvoiceAddress,
                  selectedDeliveryAddress,
                  setSelectedInvoiceAddress,
                  setSelectedDeliveryAddress,
                  switchToggle
                );
              }
              resolve(true);
            } catch (err) {
              reject(err);
            }
          },
          onCancel: () => resolve(false),
        });
      });
    } else {
      if (posConfig?.disableOfflineBilling === "Y" && !navigator.onLine) {
        errorMessage("Sync Failed", "Please check your network connection and try again");
      } else {
        await createCustomer(
          data,
          cart,
          setCart,
          form,
          tillLayout,
          closeCustomerSearch,
          tillData,
          posLogActivity,
          setKioskUI,
          setDisplayAddNewCustomer,
          UAECustomerForm,
          customerForm,
          setTillLink,
          setCustomerLoading,
          setDisplayUAECustomer,
          isCallCenterFlow,
          setCallCenterVisible,
          setIsCallCenterFlow,
          history,
          setIsSubmittingCustomer,
          setLastSearchedValue,
          setSelectedB2BCustomer,
          selectedB2BCustomer,
          setSwitchToggle,
          setFormDataCache,
          isPanCustomer,
          setIsPanCustomer,
          isB2BEnabled,
          setPanCustomerInfo,
          panCustomerInfo,
          pendingRefundCustomer,
          setPendingRefundCustomer,
          isRefundCustomerEdit,
          setIsRefundCustomerEdit,
          isRefundWalletOpen,
          setIsRefundWalletOpen,
          isNriBilling,
          setIsNriBilling,
          isCreditSale,
          setIsCreditSale,
          panDrawerMode,
          documents,
          setGstInfo,
          retailCustomerFields,
          gstInfo,
          selectedInvoiceAddress,
          selectedDeliveryAddress,
          setSelectedInvoiceAddress,
          setSelectedDeliveryAddress,
          switchToggle
        );
      }
    }
    } finally {
      customerSubmitInFlightRef.current = false;
    }
  };

  // We need a local setTillLink stub since the original references a shared state
  // This is passed through as a dependency or can be a no-op if not wired
  const setTillLink = (val) => {
    // no-op -- tillLink state lives in parent; wire this dependency if needed
  };

  // ---- Edit customer ----
  const showEditOldCustomerFields = async (customer) => {
    setDisplayCustomerSearch(false);
    // Editing existing customer — clear the "add new" flag
    setIsAddNewCustomer(false);
    setSelectedEditOldCustomer(customer);
    if (!customer) {
      console.error("Customer data not available");
      return;
    }

    const fieldValues = {
      editName: customer.name || "",
      editMobile: customer.mobileNo || "",
      editEmail: customer.email || "",
      editPincode: customer.pincode || "",
      name: customer.name || "",
      mobile: customer.mobileNo || "",
      birthday: customer.birthday ? dayjs(customer.birthday) : null,
      anniversaryDate: customer.anniversaryDate ? dayjs(customer.anniversaryDate) : null,
      city: customer.customerAddress?.line2 || "",
      street: customer.customerAddress?.line1 || "",
      gender: customer.gender || "",
      lastName: customer.lastName || "",
      taxID: customer.taxId || "",
      pincode: customer.pincode || "",
      country: customer.customerAddress?.country || tillData.tillAccess.csBunit.customerAddress?.csCountry?.name || "",
      state: customer.customerAddress?.region || tillData.tillAccess.csBunit.customerAddress?.csRegion?.name || "",
      type: "editCustomerForm",
    };

    form.setFieldsValue(fieldValues);
    setKeyboardInputFields(fieldValues);

    try {
      let loyaltyData = await db.loyalityData.toArray();
      loyaltyData.forEach((item) => {
        if (item.isDefault === "Y") {
          form.setFieldsValue({
            program: item.loyaltylevelId || "",
          });
        }
      });
    } catch (error) {
      console.error("Error fetching loyalty data:", error);
    }

    setDisplayEditOldCustomer(true);
  };

  const editOldCustomer = async (data) => {
    if (customerSubmitInFlightRef.current) return;
    customerSubmitInFlightRef.current = true;
    try {
    const isExchange = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
    const activeCart = isExchange ? useCartStore.getState().exchangeCart : cart;
    const resolvedPanDrawerMode = panDrawerMode === "THRESHOLD" && isPanCustomer && panCustomerInfo?.realStatus === "VALID & OPERATIVE" ? "VALIDATION" : panDrawerMode;
    const oldCustomer = isExchange ? activeCart?.customer : selectedEditOldCustomer;
    await updateCustomer(
      data,
      activeCart,
      setCart,
      form,
      closeCustomerSearch,
      oldCustomer,
      setSelectedEditOldCustomer,
      setDisplayEditOldCustomer,
      tillData,
      activeCart?.customer,
      UAECustomerForm,
      setDisplayAddNewCustomer,
      setFormDataCache,
      setIsSubmittingCustomer,
      setLastSearchedValue,
      setSelectedB2BCustomer,
      selectedB2BCustomer,
      isCallCenterFlow,
      setCallCenterVisible,
      setIsCallCenterFlow,
      history,
      tillLayout,
      isPanCustomer,
      setIsPanCustomer,
      isB2BEnabled,
      setPanCustomerInfo,
      panCustomerInfo,
      pendingRefundCustomer,
      setPendingRefundCustomer,
      isRefundCustomerEdit,
      setIsRefundCustomerEdit,
      isRefundWalletOpen,
      setIsRefundWalletOpen,
      isNriBilling,
      setIsNriBilling,
      isCreditSale,
      setIsCreditSale,
      resolvedPanDrawerMode,
      documents,
      setGstInfo,
      gstInfo,
      null,
      selectedInvoiceAddress,
      selectedDeliveryAddress,
      setSelectedInvoiceAddress,
      setSelectedDeliveryAddress,
      switchToggle,
      setSwitchToggle
    );
    // Reset PAN state after successful update so next flow triggers validation fresh
    setPanDrawerMode(null);
    setIsPanUpdateMode(false);
    } finally {
      customerSubmitInFlightRef.current = false;
    }
  };

  // ---- Remove customer ----
  const removeCutomer = async () => {
    if (customerSelectionInFlightRef.current) return;
    if (cart.payments.length > 0) {
      message.warning(`${messages.errors.cart.delete_payment_first_customer}`);
      return;
    }
    customerSelectionInFlightRef.current = true;
    try {
    setCustomerFlag(true);
    localStorage.removeItem("isB2bEnabled");
    useTransactionStore.getState().setIsB2bEnabled(null);
    localStorage.removeItem("isPanCustomer");
    localStorage.removeItem("isNriBilling");
    localStorage.removeItem("isCreditSale");
    useTransactionStore.getState().setIsCreditSale(null);
    localStorage.removeItem("customerAddressIds");
    let latestData = await removeAllDiscounts(false, true);
    let cartObj = {
      ...latestData,
      customer: defaultCustomer,
    };
    let updatedCart = cartObj;
    updatedCart = await handleDeleteLoyaltyPayment(updatedCart, latestData.customer);
    if (cartObj.items?.length > 0) {
      await Promise.all(
        cartObj.items.map(async (ele) => {
          let addToCart = ele;
          let matchedPriceIndex = [];
          if (defaultCustomer?.cwrCustomerId !== latestData?.customer?.cwrCustomerId) {
            const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
            let taxIncludeFlagLocal =
              saleTypeData?.customer?.istaxincluded === "Y" ? "Y" : tillData.tillAccess.csBunit.isTaxIncluded ? tillData.tillAccess.csBunit.isTaxIncluded : "Y";

            if (addToCart?.priceList?.length > 0) {
              // TIC-5459: also skip when a Total Bill Discount is active — this early return
              // already skips both the price-list reset AND the mrp-discount recompute below,
              // so an item under an active TD discount keeps removeAllDiscounts' correct
              // nettotal/taxAmount instead of getting double-subtracted here.
              const hasManualDiscount =
                updatedCart?.manualDiscountData?.lineLevelDiscount?.length > 0 || updatedCart?.manualDiscountData?.totalLevelDiscount?.length > 0;

              if (hasManualDiscount) {
                return;
              }
              const selectedPrice = getApplicablePrice({
                priceList: addToCart.priceList,
                selectedUomId: addToCart.uom || addToCart.selectedUomId || addToCart.uomData?.[0]?.csUomId,
                sPriceListId: updatedCart.customer.sPriceListId,
                alternateUom: addToCart?.alternateUom || [],
                primaryUomId: addToCart?.primaryUomId || addToCart?.csUomId || addToCart?.uomData?.[0]?.csUomId || null,
              });

              if (selectedPrice && addToCart?.posPriceEdit !== "Y" && !(shouldUseBatchPrice(addToCart) && addToCart.mBatchId)) {
                addToCart.price = selectedPrice.pricestd;
                addToCart.salePrice = selectedPrice.pricestd;
                addToCart.realPrice = selectedPrice.pricestd;
                addToCart.sunitprice = selectedPrice.pricestd;
              } else if (!updatedCart.customer.sPriceListId && !(shouldUseBatchPrice(addToCart) && addToCart.mBatchId)) {
                addToCart.price = ele?.originalPrice || ele?.sunitprice;
                addToCart.salePrice = ele?.children?.length > 0 ? ele.realPrice : ele?.originalPrice || ele?.sunitprice;
                addToCart.realPrice = ele?.children?.length > 0 ? ele.realPrice : ele?.originalPrice || ele?.sunitprice;
                addToCart.sunitprice = ele?.originalPrice || ele?.sunitprice;
              }
            }
            if (Array.isArray(addToCart.children) && addToCart.children.length > 0) {
              addToCart.children.forEach((child) => {
                if (child.priceList?.length > 0 && !child.mBatchId) {
                  const childPrice = getApplicablePrice({
                    priceList: child.priceList,
                    selectedUomId: child.uom || child.selectedUomId || child.uomData?.[0]?.csUomId,
                    sPriceListId: updatedCart.customer.sPriceListId,
                    alternateUom: child?.alternateUom || [],
                    primaryUomId: child?.primaryUomId || child?.csUomId || child?.uomData?.[0]?.csUomId || null,
                  });
                  if (childPrice && !(shouldUseBatchPrice(child) && child.mBatchId)) {
                    child.price = childPrice.pricestd;
                    child.salePrice = childPrice.pricestd;
                    child.realPrice = childPrice.pricestd;
                    child.sunitprice = childPrice.pricestd;
                  } else if (!updatedCart.customer.sPriceListId && !(shouldUseBatchPrice(child) && child.mBatchId)) {
                    child.price = child.originalPrice || child.sunitprice;
                    child.salePrice = child.originalPrice || child.sunitprice;
                    child.realPrice = child.originalPrice || child.sunitprice;
                    child.sunitprice = child.originalPrice || child.sunitprice;
                  }
                }
                const sp = parseFloat(child.sunitprice);
                const childMrp = sp * child.weight;
                const childTax = taxIncludeFlagLocal === "Y" ? childMrp - childMrp / (1 + child.taxRate / 100) : (childMrp * child.taxRate) / 100;
                child.taxAmount = childTax;
                child.nettotal = taxIncludeFlagLocal === "Y" ? parseFloat(childMrp.toFixed(precision)) : parseFloat((childMrp + childTax).toFixed(precision));
              });
              addToCart.taxAmount = sumBy(addToCart.children, "taxAmount");
              addToCart.nettotal = sumBy(addToCart.children, "nettotal");
            } else if (addToCart?.priceList?.length > 0) {
              const addOnsPriceSum = sumBy(addToCart?.selectedAddons, "price");
              const mrp = (parseFloat(addToCart.salePrice) + addOnsPriceSum * addToCart.weight) * parseFloat(addToCart.weight);
              const tax = taxIncludeFlagLocal === "Y" ? mrp - mrp / (1 + addToCart.taxRate / 100) : mrp * (addToCart.taxRate / 100);
              addToCart.taxAmount = tax;
              addToCart.nettotal =
                taxIncludeFlagLocal === "Y"
                  ? parseFloat(parseFloat(mrp - parseFloat(addToCart.discount ? addToCart.discount : 0))?.toFixed(precision))
                  : parseFloat(parseFloat(mrp - parseFloat(addToCart.discount ? addToCart.discount : 0))?.toFixed(precision)) + tax;
            }
          }
          addToCart.nettotal = parseFloat(addToCart.nettotal.toFixed(precision));
          updatedCart = await pricingRuleController(addToCart, cartObj, cartObj, setCart, cartObj, orderType);
          return updatedCart;
        })
      );
    } else {
      let orderTimeDetails = usePOSConfigStore.getState().orderTimeDetails || { orderStartTime: "", orderEndTime: "", paymentStartTime: "" };
      orderTimeDetails = {
        ...orderTimeDetails,
        orderStartTime: "",
      };
      usePOSConfigStore.getState().setOrderTimeDetails(orderTimeDetails);
    }

    if (cartObj.totalDiscountFlag) {
      await openPaymentModalByCustomer(updatedCart);
    }

    if (updatedCart?.couponInput?.length > 0) {
      let addToCart = cartObj.items[0];
      await Promise.all(
        updatedCart.couponInput.map(async (coupon) => {
          const matchingPricingRules = findPricingRuleById(coupon.mPricingruleId);
          if (matchingPricingRules[0].type !== "TD" && matchingPricingRules[0].type !== "TDF") {
            updatedCart = await pricingRuleController(
              addToCart,
              updatedCart,
              cart,
              setCart,
              cartRef,
              orderType,
              true,
              coupon.couponCode,
              coupon.referenceId,
              coupon.mPricingCouponId,
              coupon.mPricingruleId
            );
          } else {
            updatedCart = await processBillDiscounts(
              matchingPricingRules[0],
              updatedCart,
              true,
              coupon.couponCode,
              coupon.referenceId,
              coupon.mPricingCouponId,
              coupon.mPricingruleId
            );
          }
        })
      );
    }

    if (updatedCart?.manualDiscountData?.lineLevelDiscount?.length > 0) {
      for (const item of updatedCart.manualDiscountData.lineLevelDiscount) {
        // Fall back to the always-loaded pricing-rules cache: manualDiscountTypes state is only
        // populated when the Manual Discount modal is opened, so on a fresh load (or restored cart)
        // it can be empty — which would silently drop the manual line discount on re-apply.
        const pricingRule =
          manualDiscountTypes.find((md) => md.mPricingrulesId === item.pricingRule) ||
          getPricingRules().find((r) => r.mPricingrulesId === item.pricingRule);
        let productSelected = updatedCart.items.filter((cartItem) => cartItem.productId === item.productId);
        if (pricingRule?.type === "FD") {
          try {
            const newCart = await CheckoutFlatDiscount(productSelected[0], pricingRule, setCart, updatedCart, orderType, updatedCart, item.discountValue);
            updatedCart = newCart;
          } catch (err) {
            console.error("Error applying flat discount:", err);
          }
        } else if (pricingRule?.type === "PD") {
          try {
            const newCart = await CheckoutPercentageDiscount(productSelected[0], pricingRule, setCart, updatedCart, orderType, updatedCart, item.discountValue);
            updatedCart = newCart;
          } catch (err) {
            console.error("Error applying percentage discount:", err);
          }
        }
      }
    }

    if (updatedCart?.manualDiscountData?.totalLevelDiscount?.length > 0) {
      for (const item of updatedCart.manualDiscountData.totalLevelDiscount) {
        // Fall back to the always-loaded pricing-rules cache: manualDiscountTypes state is only
        // populated when the Manual Discount modal is opened, so on a fresh load (or restored cart)
        // it can be empty — which would silently drop the manual line discount on re-apply.
        const pricingRule =
          manualDiscountTypes.find((md) => md.mPricingrulesId === item.pricingRule) ||
          getPricingRules().find((r) => r.mPricingrulesId === item.pricingRule);
        if (pricingRule?.type === "TD") {
          try {
            const newCart = await CheckoutTotalManualDiscount(pricingRule, setCart, updatedCart, orderType, updatedCart, item.discountValue);
            updatedCart = newCart;
          } catch (err) {
            console.error("Error applying Total discount:", err);
          }
        }
      }
    }

    const itemSummery = {
      decimalItems: { totalQty: 0, totalAmount: 0 },
      nonDecimalItems: { totalQty: 0, totalAmount: 0 },
      itemsQty: 0,
      items: 0,
    };

    const isInterState2 = getIsInterState(updatedCart);
    const groupedItemsByTaxRate = groupItemsByTaxRate(updatedCart.items, precision, { isInterState: isInterState2 });

    let updatedTotalTax = 0;
    let updatedTotalPrice = 0;
    let updatedTotalItemsQty = 0;
    let updatedTotalDiscounts = 0;

    updatedCart.items = updatedCart.items.map((item, i) => {
      const precisionVal = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
      const nettotalFixed = parseFloat(parseFloat(item.nettotal)?.toFixed(precisionVal));
      const taxAmountFixed = parseFloat(parseFloat(item.taxAmount)?.toFixed(precisionVal));
      const discountFixed = item.discount ? parseFloat(parseFloat(item.discount)?.toFixed(precisionVal)) : 0;

      updatedTotalPrice += item.nettotal || 0;
      updatedTotalItemsQty += item.weight;
      updatedTotalTax += item.taxAmount || 0;
      updatedTotalDiscounts += discountFixed;

      item.discount = discountFixed;
      item.key = i;

      if (!item.isGiftCard) {
        const unitPrice = isFinite(item.nettotal / item.weight) ? (taxIncludeFlag === "Y" ? item.nettotal / item.weight / (1 + item.taxRate / 100) : item.salePrice) : 0;

        const lineTax = item.listPrice * (1 + item.taxRate / 100);
        const stdTax = item.sunitprice * (1 + item.taxRate / 100);
        const grossUnit = Math.abs(item.sunitprice) - Math.abs(item.discount) / Math.abs(item.weight);
        const netList =
          taxIncludeFlag === "Y" ? item.listPrice - parseFloat(parseFloat(item.listPrice - item.listPrice / (1 + item.taxRate / 100))?.toFixed(precisionVal)) : item.listPrice;
        const gross_list = taxIncludeFlag === "Y" ? item.listPrice : parseFloat(parseFloat(lineTax)?.toFixed(precisionVal));
        const grossStd = taxIncludeFlag === "Y" ? item.sunitprice : parseFloat(parseFloat(stdTax)?.toFixed(precisionVal));

        Object.assign(item, {
          linetax: nettotalFixed ? taxAmountFixed : 0,
          linenet: nettotalFixed ? parseFloat(parseFloat(item.nettotal - item.taxAmount)?.toFixed(precisionVal)) : 0,
          linegross: nettotalFixed || 0,
          linestd: nettotalFixed ? parseFloat(parseFloat(grossStd * item.weight)?.toFixed(precisionVal)) : 0,
          netunit: nettotalFixed ? parseFloat(parseFloat(unitPrice)?.toFixed(precisionVal)) : 0,
          listprice: nettotalFixed ? item.listPrice : 0,
          grossunit: nettotalFixed ? parseFloat(parseFloat(unitPrice)?.toFixed(precisionVal)) * (item.isReturn ? -1 : 1) : 0,
          grossstd: nettotalFixed ? grossStd : 0,
          grosslist: nettotalFixed ? gross_list : 0,
          netList: nettotalFixed ? netList : 0,
          unitPrice: nettotalFixed ? parseFloat(parseFloat(unitPrice)?.toFixed(precisionVal)) : 0,
          taxAmount: nettotalFixed ? taxAmountFixed : 0,
        });
      }

      // Update summary
      if (Array.isArray(item.children) && item.children.length > 0) {
        item.children.forEach((child) => {
          const isDecimal = child.uomData?.[0]?.decimal === "Y" || child.isDecimal;
          const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";

          itemSummery.itemsQty += child.weight;
          itemSummery.items += 1;
          itemSummery[summaryKey].totalQty += child.weight;
          itemSummery[summaryKey].totalAmount += parseFloat(parseFloat(child.nettotal)?.toFixed(precisionVal));
        });
      } else {
        const isDecimal = item.uomData?.[0]?.decimal === "Y" || item.isDecimal;
        const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";

        itemSummery.itemsQty += item.weight;
        itemSummery.items += 1;
        itemSummery[summaryKey].totalQty += item.weight;
        itemSummery[summaryKey].totalAmount += parseFloat(parseFloat(item.nettotal)?.toFixed(precisionVal));
      }
      return item;
    });

    const sumLineTotals = (lines) => {
      let totalLineGross = 0;
      let totalLineTax = 0;

      lines.forEach((line) => {
        totalLineGross += line.nettotal;
        totalLineTax += line.taxAmount;
      });

      return { totalLineGross, totalLineTax };
    };
    const adjustOrderTotals = (order, lines) => {
      const { totalLineGross, totalLineTax } = sumLineTotals(lines);
      let adjustedOrderGross = order.total;
      let adjustedOrderTax = order.tax;

      const grossDifference = totalLineGross - adjustedOrderGross;
      const taxDifference = totalLineTax - adjustedOrderTax;

      // If grossDifference equals the existing roundOff, the gap is due to intentional
      // currency rounding — preserve the rounded total instead of correcting it back.
      // grossDifference = gross - rounded; roundOff = rounded - gross; sum = 0 when equal.
      const existingRoundOff = order.roundOff || 0;
      const isRoundingDifference = Math.abs(grossDifference + existingRoundOff) < 0.01;

      if (Math.abs(grossDifference) >= 0.005 && !isRoundingDifference) {
        adjustedOrderGross += grossDifference;
      }

      if (Math.abs(taxDifference) >= 0.005) {
        adjustedOrderTax += taxDifference;
      }

      return {
        ...order,
        total: adjustedOrderGross,
        tax: adjustedOrderTax,
      };
    };
    updatedCart = adjustOrderTotals(updatedCart, updatedCart.items);
    const roundingMethod = getRoundingMethod(tillData);
    let finalTotal = updatedCart.total;
    let finalRoundOff = updatedCart.roundOff || 0;
    if (roundingMethod !== "NR") {
      const { roundedTotal, roundOffAmount } = applyCurrencyRounding(updatedTotalPrice, roundingMethod, getPrecision(tillData));
      finalTotal = roundedTotal;
      finalRoundOff = parseFloat(parseFloat(roundOffAmount).toFixed(precision));
    }
    const allItemsInCart = (updatedCart.items || []).flatMap((item) => (Array.isArray(item.children) && item.children.length > 0 ? item.children : [item]));
    const updatedTotalStd = allItemsInCart.reduce((sum, item) => sum + (item.linestd || 0), 0);
    let finalCartObj = {
      ...updatedCart,
      items: [...updatedCart.items],
      discount: updatedTotalDiscounts,
      totalQty: updatedTotalItemsQty,
      total: finalTotal,
      roundOff: finalRoundOff,
      tax: roundTo(updatedTotalTax, getPrecision(tillData)),
      totalStd: updatedTotalStd,
      groupedItemsByTaxRate,
    };
    delete finalCartObj.isRetrived;
    delete finalCartObj.parked;
    if (isExchange) {
      useCartStore.getState().setExchangeCart(finalCartObj);
    } else {
      setCart({ ...finalCartObj });
    }
    hasShownCustomerModalRef.current = false;
    setShowPaymentMethods(false);
    setIsNriBilling(false);
    setIsPanCustomer(false);
    setPanDrawerMode(null);
    localStorage.removeItem("isPanCustomer");
    localStorage.removeItem("isNriBilling");
    useTransactionStore.getState().setIsB2bEnabled(null);
    useTransactionStore.getState().setIsCreditSale(null);
    upsertPOSLog(cart, "RCT");
    } finally {
      customerSelectionInFlightRef.current = false;
    }
  };

  return {
    // State
    displayCustomerSearch,
    setDisplayCustomerSearch,
    displayUAECustomerSearch,
    setDisplayUAECustomerSearch,
    displayUAECustomer,
    setDisplayUAECustomer,
    customerSearchType,
    setCustomerSearchType,
    customerSearchInput,
    setCustomerSearchInput,
    customerSearchResults,
    setCustomerSearchResults,
    closeCustomerFlag,
    setCloseCustomerFlag,
    customerSearchTrigger,
    setCustomerSearchTrigger,
    lastSearchedValue,
    setLastSearchedValue,
    customerLoading,
    setCustomerLoading,
    customerFlag,
    setCustomerFlag,
    displayAddNewCustomer,
    setDisplayAddNewCustomer,
    displayEditOldCustomer,
    setDisplayEditOldCustomer,
    selectedEditOldCustomer,
    setSelectedEditOldCustomer,
    isPanCustomer,
    setIsPanCustomer,
    panCustomerInfo,
    setPanCustomerInfo,
    isPanUpdateMode,
    setIsPanUpdateMode,
    isAddNewCustomer,
    setIsAddNewCustomer,
    isNriBilling,
    setIsNriBilling,
    panDrawerMode,
    setPanDrawerMode,
    documents,
    setDocuments,
    uploadedDocuments,
    setUploadedDocuments,
    uploadModalVisible,
    setUploadModalVisible,
    isCreditSale,
    setIsCreditSale,
    isB2BEnabled,
    setIsB2BEnabled,
    selectedB2BCustomer,
    setSelectedB2BCustomer,
    switchToggle,
    setSwitchToggle,
    selectedInvoiceAddress,
    setSelectedInvoiceAddress,
    selectedDeliveryAddress,
    setSelectedDeliveryAddress,
    formDataCache,
    setFormDataCache,
    isViewMode,
    setIsViewMode,
    viewCustomerValues,
    setViewCustomerValues,
    isSubmittingCustomer,
    setIsSubmittingCustomer,
    isCustomerDrawerOpen,
    setIsCustomerDrawerOpen,
    expandedGroups,
    setExpandedGroups,
    kioskUI,
    setKioskUI,
    kioskLogin,
    layoutType,
    setLayoutType,
    pendingRefundCustomer,
    setPendingRefundCustomer,
    isRefundCustomerEdit,
    setIsRefundCustomerEdit,
    isRefundWalletOpen,
    setIsRefundWalletOpen,
    gstInfo,
    setGstInfo,
    activeTab,
    setActiveTab,
    activeTabName,
    setActiveTabName,
    UAECustomerForm,
    customerForm,
    customerSearchInputRef,

    // Functions
    closeCustomerSearch,
    handleCustomerSearch,
    proceedWithCustomerSelection,
    selectCustomer,
    showAddNewCustomerFields,
    showAddNewUAECustomerFields,
    addNewCustomer,
    showEditOldCustomerFields,
    editOldCustomer,
    removeCutomer,
    validateAndSavePanForCustomer,
    extractPanFromMeta,
    getPanRealStatus,
    autoEnablePanCustomer,
    autoEnableNriBilling,
    convertMetaDataToKeyValue,
    toCamelCaseMetaData,
    normalizeStaticSelectValue,
  };
};

export default useCustomerOperations;
